import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Edit, Delete } from "@mui/icons-material";
import { message } from "antd";

const ViewStudent = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = React.useState(null);
  const [academicYearTuition, setAcademicYearTuition] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const token = localStorage.getItem("token");
  const direction = ["ar", "ku"].includes(i18n.language) ? "rtl" : "ltr";
  const textAlign = ["ar", "ku"].includes(i18n.language) ? "right" : "left";

  const parseJsonField = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return parsed[i18n.language] || parsed.en || t("notAvailable");
    } catch (e) {
      return t("notAvailable");
    }
  };

  const fetchStudent = async () => {
    try {
      const response = await axios.get(
        `https://api.clever-school.com/api/students/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const studentData = response.data;
      const tuitionData = studentData.tuitions?.[0] || null;

      setStudent(studentData);
      setAcademicYearTuition(tuitionData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching student:", error);
      setError(t("error.fetchingData"));
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t, token]);

  const handlePostTuition = () => navigate(`/${id}/create-tuition`);

  const handleUpdateTuition = () => {
    if (academicYearTuition) {
      navigate(`/update-tuition/${academicYearTuition.id}`, {
        state: { tuitionData: academicYearTuition },
      });
    }
  };

  // const handleEditPayment = (paymentId) => {
  //   navigate(`/Update_PaymentStudent/${paymentId}`);
  // };
  const handleEditPayment = (payment) => {
    navigate(`/Update_PaymentStudent/${payment.id}`, {
      state: {
        paymentData: payment,
        studentId: id, // Pass student ID as well
      },
    });
  };
  const handleDeletePayment = async (paymentId) => {
    try {
      await axios.delete(
        `https://api.clever-school.com/api/payments/${paymentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      message.success(t("paymentDeletedSuccess"));
      fetchStudent(); // Refresh the data
    } catch (error) {
      console.error("Error deleting payment:", error);
      message.error(t("paymentDeleteError"));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="h6" color="error" sx={{ m: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!student) {
    return (
      <Typography variant="h6" sx={{ m: 2 }}>
        {t("studentNotFound")}
      </Typography>
    );
  }

  // Calculate total paid amount
  const totalPaid =
    student.tuition_payments?.reduce(
      (sum, payment) => sum + parseFloat(payment.amount_paid || 0),
      0
    ) || 0;

  // Calculate remaining balance
  const remainingBalance = academicYearTuition
    ? parseFloat(academicYearTuition.final_amount) - totalPaid
    : 0;

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "40px auto",
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
        direction,
        textAlign,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: 4,
          padding: theme.spacing(4),
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t("studentDetails")}
          </Typography>
          <Button
            onClick={handlePostTuition}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              textTransform: "none",
              boxShadow: theme.shadows[2],
            }}
          >
            {t("postTuition")}
          </Button>
        </Box>

        {/* Personal Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("personalInformation")}
          </Typography>
          <Grid container spacing={3}>
            {[
              { label: t("name"), value: student.name },
              {
                label: t("gender"),
                value: student.gender === "M" ? t("male") : t("female"),
              },
              { label: t("phoneNumber"), value: student.phone_number },
              {
                label: t("altPhoneNumber"),
                value: student.alt_phone_number || t("notAvailable"),
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {item.icon}
                      {item.label}
                    </Typography>
                    <Typography variant="h6">{item.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Academic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            {t("academicInformation")}
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                label: t("class"),
                value: parseJsonField(student.class_model.name),
              },
              {
                label: t("section"),
                value: parseJsonField(student.section.name),
              },
              {
                label: t("branch"),
                value: parseJsonField(student.branch.name),
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {item.icon}
                      {item.label}
                    </Typography>
                    <Typography variant="h6">{item.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Tuition Details Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {academicYearTuition?.academic_year
                ? `${t("academicYear")}: ${academicYearTuition.academic_year}`
                : t("tuitionDetails")}
            </Typography>
            {academicYearTuition && (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleUpdateTuition}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  textTransform: "none",
                  boxShadow: theme.shadows[2],
                }}
              >
                {t("updateTuition")}
              </Button>
            )}
          </Box>

          {academicYearTuition ? (
            <Grid container spacing={3}>
              {[
                {
                  label: t("tuitionFee"),
                  value: academicYearTuition.tuition_fee,
                },
                {
                  label: t("discountedStudents"),
                  value: academicYearTuition.discount_amount,
                },
                {
                  label: t("finalAmount"),
                  value: academicYearTuition.final_amount,
                },
                {
                  label: t("totalPaid"),
                  value: totalPaid,
                  color: theme.palette.success.main,
                },
                {
                  label: t("remainingBalance"),
                  value: remainingBalance,
                  color:
                    remainingBalance > 0
                      ? theme.palette.error.main
                      : theme.palette.success.main,
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 2 }}>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {item.icon}
                        {item.label}
                      </Typography>
                      <Typography variant="h6" sx={{ color: item.color }}>
                        {parseFloat(item.value).toLocaleString("en-US")}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      {t("reason")}
                    </Typography>
                    <Typography variant="body1">
                      {academicYearTuition.reason || t("notAvailable")}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              {t("noTuitionRecords")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Payment History Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
              {t("paymentHistory")}
            </Typography>
          </Box>

          {student.tuition_payments?.length > 0 ? (
            <TableContainer component={Paper} elevation={2}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("paymentDate")}</TableCell>
                    <TableCell align="right">{t("amountPaid")}</TableCell>
                    <TableCell align="right">{t("Action")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {student.tuition_payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_date}</TableCell>
                      <TableCell align="right">
                        {parseFloat(payment.amount_paid).toLocaleString(
                          "en-US"
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => handleEditPayment(payment)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeletePayment(payment.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="h6"
              color="textSecondary"
              sx={{ textAlign: "center", py: 4 }}
            >
              {t("noPaymentRecords")}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ViewStudent;
